import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Clock, Mail, CheckCircle, Zap, MessageSquare, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useState } from "react";
import { useToast } from "../hooks/use-toast";

// Sample services for demo results
const sampleServices = [
  {
    id: 1,
    name: "ChatBot Pro",
    tagline: "24/7 AI Customer Support",
    category: "Customer Support",
    price: "$49/mo",
    setupTime: "1 day",
    highlight: "Multi-channel",
    matchScore: 95,
    features: ["Multi-channel support", "Custom workflows", "50+ integrations", "Analytics dashboard"],
    description: "Enterprise-grade AI customer support that handles inquiries automatically. Reduce support costs while improving customer satisfaction."
  },
  {
    id: 2,
    name: "SupportGenius AI",
    tagline: "Intelligent Ticket Resolution",
    category: "Customer Support",
    price: "$79/mo",
    setupTime: "2 days",
    highlight: "Smart Routing",
    matchScore: 88,
    features: ["Smart routing", "Sentiment analysis", "Knowledge base AI", "Team collaboration"],
    description: "Empower your support team with AI that learns from every interaction. Automatically categorize, prioritize, and route tickets to the right agents."
  },
  {
    id: 3,
    name: "HelpDesk AI",
    tagline: "Self-Service Support Platform",
    category: "Customer Support",
    price: "$39/mo",
    setupTime: "Same day",
    highlight: "Quick Setup",
    matchScore: 82,
    features: ["AI knowledge base", "Chatbot builder", "Email automation", "Live chat"],
    description: "Build a comprehensive self-service portal powered by AI. Help customers find answers instantly while reducing ticket volume."
  }
];

const DemoResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [showContactForm, setShowContactForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [contactForm, setContactForm] = useState({
    businessName: "",
    email: "",
    phone: "",
    message: "",
    companySize: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const searchQuery = location.state?.query || "AI solutions";
  const isFromAssessment = location.state?.fromAssessment || false;

  const handleNotifyMe = async (e) => {
    e.preventDefault();
    if (!email) return;
    toast({
      title: "You're on the list!",
      description: "We'll notify you as soon as we launch.",
    });
    setEmail("");
  };

  const handleBookDemo = (service) => {
    setSelectedService(service);
    setShowContactForm(true);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.businessName || !contactForm.email) {
      toast({ title: "Please fill in required fields", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submissions/lead-submission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: selectedService?.name || "AI Service",
          businessName: contactForm.businessName,
          businessEmail: contactForm.email,
          businessSize: contactForm.companySize,
          phone: contactForm.phone,
          message: contactForm.message
        }),
      });

      toast({
        title: "Demo Request Sent! 🎉",
        description: "The AI provider will contact you within 24 hours.",
      });
      setShowContactForm(false);
      setContactForm({ businessName: "", email: "", phone: "", message: "", companySize: "" });
    } catch (error) {
      toast({
        title: "Request Sent!",
        description: "We'll connect you with the provider shortly.",
      });
      setShowContactForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#121212]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#121212]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
            <span className="text-xl font-bold">AI Fit Engine</span>
          </button>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate('/browse')}>
              Browse All
            </Button>
            <Button onClick={() => navigate('/assessment')} className="bg-orange-500 hover:bg-orange-600">
              Take Assessment
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-8">
        
        {/* Search Summary */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-3 py-1 rounded-full text-sm font-medium">
              {isFromAssessment ? "Assessment Results" : "Search Results"}
            </span>
            <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-sm font-medium">
              Demo Mode
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {isFromAssessment ? "Your Perfect AI Matches" : `AI Services for "${searchQuery}"`}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {sampleServices.length} AI services matched to your needs • Sorted by relevance
          </p>
        </div>

        {/* Demo Notice */}
        <div className="mb-8 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Zap size={24} />
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">🎉 This is a Feature Preview</h3>
              <p className="text-orange-50">
                You're seeing sample AI services. When we launch, you'll see real AI startups matched to your specific needs.
                <strong> Booking demos and contacting providers is fully functional!</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Results - Fiverr Style Service Cards */}
        <div className="space-y-6">
          {sampleServices.map((service, index) => (
            <div
              key={service.id}
              className="bg-white dark:bg-[#1E1E1E] rounded-xl shadow-sm hover:shadow-lg transition-all border border-gray-100 dark:border-gray-800 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Service Logo */}
                  <div className="flex items-start gap-4 lg:w-56 flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center text-white text-xl font-bold">
                      {service.name.charAt(0)}
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">{service.category}</span>
                      <span className="block mt-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full">
                        {service.highlight}
                      </span>
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                          {service.name}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">{service.tagline}</p>
                      </div>
                      <div className="text-right">
                        <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-3 py-1 rounded-full text-sm font-bold">
                          {service.matchScore}% Match
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {service.features.map((feature, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full">
                          <CheckCircle size={14} className="text-green-500" />
                          {feature}
                        </span>
                      ))}
                    </div>

                    {/* Stats Row */}
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>Setup: {service.setupTime}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageSquare size={14} />
                        <span>Direct contact available</span>
                      </div>
                    </div>
                  </div>

                  {/* Price & CTA */}
                  <div className="lg:w-48 flex-shrink-0 flex flex-col items-end justify-between">
                    <div className="text-right mb-4">
                      <p className="text-sm text-gray-500">Starting at</p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{service.price}</p>
                    </div>
                    <div className="flex flex-col gap-2 w-full lg:w-auto">
                      <Button
                        onClick={() => handleBookDemo(service)}
                        className="bg-orange-500 hover:bg-orange-600 text-white w-full"
                      >
                        Book Demo
                      </Button>
                      <Button variant="outline" className="w-full">
                        <ExternalLink size={16} className="mr-2" />
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Can't find what you're looking for?</h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Take our comprehensive assessment and we'll match you with the perfect AI solution for your specific needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/assessment')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Take Assessment
            </Button>
            <Button
              onClick={() => navigate('/browse')}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              Browse All Services
            </Button>
          </div>
        </div>

        {/* Email Signup */}
        <div className="mt-8 bg-white dark:bg-[#1E1E1E] rounded-xl p-8 border border-gray-200 dark:border-gray-800 text-center">
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">Get notified when we launch</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Be the first to access our full AI marketplace</p>
          <form onSubmit={handleNotifyMe} className="flex gap-3 max-w-md mx-auto">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1"
              required
            />
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
              <Mail size={18} className="mr-2" />
              Notify Me
            </Button>
          </form>
        </div>
      </div>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">Book a Demo</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">with {selectedService?.name}</p>
              </div>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={contactForm.businessName}
                  onChange={(e) => setContactForm({ ...contactForm, businessName: e.target.value })}
                  placeholder="Your company name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone (optional)
                </label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company Size
                </label>
                <select
                  value={contactForm.companySize}
                  onChange={(e) => setContactForm({ ...contactForm, companySize: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Select size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-1000">201-1000 employees</option>
                  <option value="1000+">1000+ employees</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message (optional)
                </label>
                <Textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="Tell them about your needs..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                >
                  {isSubmitting ? "Sending..." : "Request Demo"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoResultsPage;
